// pages/master/company/MasterCompanyPage.jsx
'use client';

import { useState, useEffect, useMemo } from 'react';
import DataTable from '../../../components/DataTable';
import ActionMenu from '../../../components/ActionMenu';
import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';

import { useCompanyDataList, deleteCompany } from '../../../stores/companyStore';
import MasterCompanyAddEdit from './MasterCompanyAddEdit';
import { useLanguage } from '../../../utils/i18n';
import { fleetApi } from '../../../api/axiosClient';

function MasterCompanyPageContent() {
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const notify = useNotifier();
  const { t } = useLanguage();

  // --- 1. SEARCH STATES ---
  const [searchField, setSearchField] = useState('name');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState(''); // Debounced value

  // STATE LOKAL UNTUK PENCARIAN 'ALL'
  const [localCompanies, setLocalCompanies] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localTotal, setLocalTotal] = useState(0);

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') }, // Opsi pencarian global
      { value: 'name', label: t('company.name', 'Company Name') },
      { value: 'code', label: t('company.code', 'Company Code') },
      {
        value: 'business_type',
        label: t('masterCompany.businessType', 'Business Type'),
        tableName: 'm_business_type',
        fieldName: 'name',
        joinKey: 'business_type'
      },
      { value: 'country', label: t('masterCompany.country', 'Country') },
      { value: 'city', label: t('company.city', 'City') }
    ],
    [t]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchValue(String(searchRaw ?? '').trim());
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);

    return () => clearTimeout(timeoutId);
  }, [searchRaw, searchField]);

  const hookSearchField = searchField === 'all' ? '' : searchField;
  const hookSearchValue = searchField === 'all' ? '' : searchValue;

  const {
    data: companiesFromHook,
    totalRecord: hookTotal,
    isLoading: hookLoading
  } = useCompanyDataList(paginationModel.page, paginationModel.pageSize, hookSearchField, hookSearchValue);

  const rows = searchField === 'all' ? localCompanies : companiesFromHook || [];
  const isLoading = searchField === 'all' ? localLoading : hookLoading;
  const totalRecords = searchField === 'all' ? localTotal : hookTotal;

  useEffect(() => {
    if (searchField !== 'all') return;
    const controller = new AbortController();

    const fetchAllSearch = async () => {
      setLocalLoading(true);
      try {
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;
        const columns = [{ logic_operator: 'like', name: 'name', operator: 'AND', value: '' }];
        let filters = [];
        if (searchValue) {
          filters = [
            { name: 'name', value: searchValue },
            { name: 'code', value: searchValue },
            { name: 'city', value: searchValue },
            { name: 'country', value: searchValue },
            { name: 'name', table_name: 'm_business_type', value: searchValue }
          ];
        }

        const payload = {
          paging: { start, length },
          orders: { columns: ['name'], ascending: true },
          joins: [{ name: 'business_type', column_results: ['name'] }],
          columns: columns,
          filters: filters
        };

        const resp = await fleetApi.post('/company/search', payload, { signal: controller.signal });
        const data = resp?.data ?? null;

        const fetchedRows = Array.isArray(data) ? data : (data?.data ?? []);
        const total = Array.isArray(data) ? fetchedRows.length : (data?.totalRecord ?? data?.totalRecords ?? data?.dataSize ?? 0);

        setLocalCompanies(fetchedRows);
        setLocalTotal(total);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('company search all error', err);
      } finally {
        if (!controller.signal.aborted) setLocalLoading(false);
      }
    };

    fetchAllSearch();
    return () => controller.abort();
  }, [searchField, searchValue, paginationModel]);

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecords > 0 ? totalRecords : 9999;
      const columns = [];
      let filters = [];

      if (searchField === 'all' && searchValue) {
        columns.push({ logic_operator: 'like', name: 'name', operator: 'AND', value: '' });
        filters = [
          { name: 'name', value: searchValue },
          { name: 'code', value: searchValue },
          { name: 'city', value: searchValue },
          { name: 'country', value: searchValue },
          { name: 'name', table_name: 'm_business_type', value: searchValue }
        ];
      } else if (searchValue) {
        const opt = searchFieldOptions.find((o) => o.value === searchField);
        if (opt) {
          const searchCol = {
            name: opt.fieldName || searchField,
            logic_operator: 'like',
            operator: 'AND',
            value: searchValue
          };
          if (opt.tableName) searchCol.table_name = opt.tableName;
          columns.push(searchCol);
        }
      }

      if (columns.length === 0) {
        columns.push({ logic_operator: 'like', name: 'name', operator: 'AND', value: '' });
      }

      const payload = {
        paging: { start: 0, length },
        orders: { columns: ['name'], ascending: true },
        joins: [{ name: 'business_type', column_results: ['name'] }],
        columns: columns,
        filters: filters
      };

      const resp = await fleetApi.post('/company/search', payload);
      const data = resp?.data ?? null;
      return Array.isArray(data) ? data : (data?.data ?? []);
    } catch (err) {
      console.error('company export all error', err);
      return [];
    }
  };

  // --- 3. HANDLERS ---
  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  // --- 4. DELETE FLOW ---
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleDelete = (id, itemName) => {
    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
    setConfirmName('');
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteCompany(confirmTargetId);
      notify({ message: t('company.notifSucces', 'Company successfully deleted'), severity: 'success' });
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error(err);
      notify({ message: t('company.notifFailed', 'Failed to delete Company'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };

  const getCountryLabel = (row) => {
    if (!row) return '-';
    if (row?.country_name) return row.country_name;
    if (row?.join?.country_name) return row.join.country_name;
    return row?.country ?? '-';
  };

  const columns = [
    { field: 'name', headerName: t('company.name', 'Company Name'), flex: 1.5 },
    { field: 'code', headerName: t('company.code', 'Company Code'), flex: 1 },
    {
      field: 'business_type',
      headerName: t('company.businessType', 'Business Type'),
      flex: 1.5,
      valueGetter: (params, row) => row?.join?.business_type_name || '-'
    },
    { field: 'country', headerName: t('company.country', 'Country'), flex: 1, renderCell: (params) => getCountryLabel(params.row) },
    { field: 'state', headerName: t('masterCompany.state', 'State'), flex: 0.5, hide: true },
    { field: 'city', headerName: t('company.city', 'City'), flex: 1 },
    { field: 'address', headerName: t('masterCompany.address', 'Address'), flex: 0.5, hide: true },
    { field: 'zip_code', headerName: t('masterCompany.zipCode', 'Zip Code'), flex: 0.5, hide: true },
    { field: 'phone1', headerName: t('masterCompany.phone1', 'Phone 1'), flex: 0.5, hide: true },
    { field: 'phone2', headerName: t('masterCompany.phone2', 'Phone 2'), flex: 0.5, hide: true },
    { field: 'fax1', headerName: t('masterCompany.fax1', 'Fax 1'), flex: 0.5, hide: true },
    { field: 'fax2', headerName: t('masterCompany.fax2', 'Fax 2'), flex: 0.5, hide: true },
    { field: 'pic_name', headerName: t('masterCompany.picName', 'PIC Name'), flex: 0.5, hide: true },
    { field: 'pic_email', headerName: t('masterCompany.picEmail', 'PIC Email'), flex: 0.5, hide: true },
    { field: 'pic_mobile_no', headerName: t('masterCompany.picMobileNo', 'PIC Mobile No'), flex: 0.5, hide: true },
    {
      field: 'status',
      headerName: t('masterCompany.status', 'Status'),
      flex: 0.5,
      valueGetter: (value, row) => {
        const st = row?.status ?? null;
        if (st === null || st === undefined) return '-';
        if (typeof st === 'boolean') return st ? 'Active' : 'Not Active';
        return String(st);
      },
      hide: true
    },

    ...(currentUserRole === 'Administrator'
      ? [
          { field: 'created_by', headerName: t('masterCompany.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('masterCompany.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('masterCompany.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('masterCompany.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      headerAlign: 'center',
      width: 120,
      renderCell: (params) => (
        <ActionMenu row={params.row} onEdit={handleEditClick} onDelete={(id) => handleDelete(id, params.row.name)} mode="inline" />
      )
    }
  ];

  return (
    <div>
      <DataTable
        title={t('masterCompany.title', 'Master Company')}
        onFetchAllForExport={handleFetchAllForExport}
        rows={rows || []}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => {
          setSearchField(field);
          setSearchRaw(value ?? '');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        searchLabel={t('company.searchCompany', 'Search Company')}
        hideCompanyFilter={true}
      />

      {openDialog && (
        <MasterCompanyAddEdit
          open={openDialog}
          id={editingId}
          onClose={handleDialogClose}
          onSuccess={() => setPaginationModel((p) => ({ ...p }))}
        />
      )}

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        itemName={confirmName}
      />
    </div>
  );
}

export default function MasterCompanyPage() {
  return <MasterCompanyPageContent />;
}
